import { useSetState } from 'minimal-shared/hooks';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter, usePathname, useSearchParams } from 'src/routes/hooks';

import {
  getLastApiCallLS,
  setLastApiCallLS,
  removeTenantIdLS,
  getAccessTokenLS,
  removeAccessTokenLS,
  removeLastApiCallLS,
} from 'src/utils/localStorageUtils';

import { toast } from 'src/components/snackbar';

import { AuthContext } from '../auth-context';
import { setSession, isValidToken } from './utils';

import type { AuthState } from '../../types';

// ----------------------------------------------------------------------

/**
 * NOTE:
 * We only build demo at basic level.
 * Customer will need to do some extra handling yourself if you want to extend the logic and other features...
 */

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const { state, setState, setField } = useSetState<AuthState>({ user: null, loading: true });
  const [isTeantListView, setIsTeantListView] = useState<boolean>(false);

  const checkUserSession = useCallback(async () => {
    try {
      // const accessToken = sessionStorage.getItem(JWT_STORAGE_KEY);
      const accessToken = getAccessTokenLS();

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        setState({ user: { accessToken }, loading: false });
      } else {
        setState({ user: null, loading: false });
        setIsTeantListView(false);
      }
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false });
      setIsTeantListView(false);
    }
  }, [setState]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  // ========================================================================================================================

  const handleTeantView = useCallback((value: boolean) => {
    setIsTeantListView(value);
  }, []);
  // ========================================================================================================================

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const activeStep = pathname.includes('') ? Number(searchParams.get('step')) : null;
  const SIGNUP_STEPS = [
    'User Details',
    'Organization Details',
    'Select Plan',

    // t('FINISH'),
  ];
  const completed = activeStep === SIGNUP_STEPS.length;
  const onChangeStep = useCallback(
    (type: 'back' | 'next', step?: any) => {
      const stepNumbers = {
        back: (activeStep ?? 0) - 1,
        next: (activeStep ?? 0) + 1,
      };

      const targetStep = stepNumbers[type];
      const queryString = new URLSearchParams({ step: `${targetStep}` }).toString();

      // ✅ Match ANY route that includes `/tenant` — works for /tenant, /tenant/123, /tenant/signup, etc.
      const isTenantRoute = isTeantListView || step === true;
      const updatePath = isTenantRoute ? paths.tenant.root : paths.auth.signUp;

      const redirectPath = targetStep === 0 ? updatePath : `${updatePath}?${queryString}`;

      router.push(redirectPath);
    },
    [activeStep, router, isTeantListView]
  );

  // ========================================================================================================================

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (pathname.includes('auth/sign-up')) {
        sessionStorage.setItem('wasOnSignUp', 'true');
      } else {
        sessionStorage.removeItem('wasOnSignUp');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [pathname]);

  useEffect(() => {
    const wasOnSignUp = sessionStorage.getItem('wasOnSignUp');
    if (wasOnSignUp === 'true') {
      sessionStorage.removeItem('wasOnSignUp');
      router.replace(paths.auth.signUp);
    }
  }, []);

  // Logout after inactivity (where inactivity means no API call made for X minutes)
  useEffect(() => {
    const INACTIVITY_TIMEOUT = 8 * 60 * 60 * 1000; // 8 hours

    const checkInactivity = () => {
      const lastRecall = getLastApiCallLS();
      if (lastRecall && state.user && !pathname.includes('auth')) {
        const now = Date.now();
        if (now - Number(lastRecall) > INACTIVITY_TIMEOUT) {
          toast.error('Session expired due to inactivity. Please log in again.');
          window.postMessage({ type: 'DISCONNECT_SIP' }, '*');
          window.postMessage({ type: 'DISCONNECT_SOCKET' }, '*');
          removeAccessTokenLS();
          removeTenantIdLS();
          removeLastApiCallLS();
          localStorage.removeItem('persist:root');
          window.location.href = paths.auth.signIn;
        }
      }
    };

    // Initialize timestamp on login if not exists
    if (state.user && !getLastApiCallLS()) {
      setLastApiCallLS(Date.now().toString());
    }

    const intervalId = setInterval(checkInactivity, 60000); // Check every minute
    return () => clearInterval(intervalId);
  }, [state.user, pathname, router]);

  // ========================================================================================================================
  const memoizedValue = useMemo(
    () => ({
      user: state.user ? { ...state.user, role: state.user?.role ?? 'admin' } : null,
      checkUserSession,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      completed,
      onChangeStep,
      steps: SIGNUP_STEPS,
      activeStep: activeStep ?? 0,
      handleTeantView,
    }),
    [checkUserSession, state.user, status, activeStep]
  );
  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
